'use client';

import { useTranslations } from 'next-intl';
import {
  BentoSection1,
  HealthSpanSection,
  HeroSectionV2,
  R1WithM1Section,
} from 'components/ring-air-landing';
import { JsonLd } from 'components/shared';
import dynamic from 'next/dynamic';
import {
  generateProductSchema,
  generateBreadcrumbSchema,
} from 'lib/seo/schema';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import {
  RingPriceData,
  AirRingColors,
} from 'content/products/ring/productMap/constants';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { Currency } from 'utils/Currency';
import Image from 'next/image';
import { useState } from 'react';
import styles from './ring-client.module.css';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { MasterTimelineProvider } from 'contexts/MasterTimelineContext';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const CircleScaleClipImageV2 = dynamic(
  () =>
    import('components/ring-air-landing').then((mod) => ({
      default: mod.CircleScaleClipImageV2,
    })),
  { ssr: false },
);
const InsideOutsideV2 = dynamic(
  () =>
    import('components/ring-air-landing').then((mod) => ({
      default: mod.InsideOutsideV2,
    })),
  { ssr: false },
);
const RingAssemblyRenderCanvas = dynamic(
  () =>
    import('components/ring-air-landing').then((mod) => ({
      default: mod.RingAssemblyRenderCanvas,
    })),
  { ssr: false },
);
const RingRotateRevolveRenderCanvas = dynamic(
  () => import('components/ring-air-landing/RingRotateRevolveRenderCanvas'),
  { ssr: false },
);
import { getAssetUrl } from 'lib/config/assetUrls';
import { usSeoImagePath } from 'lib/seo/meta/seoImage';
const AccuracySection = dynamic(
  () => import('components/ring-air-landing/AccuracySection'),
  {
    ssr: false,
  },
);

const LOADER_SRC = getCompressedAssetUrl(
  '/web_v2/site-static/splash/splash.webp',
);

const RingContent = () => {
  const [isLoading, setLoading] = useState(true);
  const t = useTranslations('home');
  const {
    region: country,
    store: regionStore,
    currency: regionCurrency,
    regionSlug,
  } = useRegionLocale();

  const countryCode = (country || 'US').toUpperCase();
  const store = regionStore ?? ShopifyStore.ROW;
  const currency = regionCurrency ?? Currency.usd;

  const defaultColor = AirRingColors.RAW_TITANIUM;
  const priceData = RingPriceData[store]?.[defaultColor];
  const actualPrice = priceData?.price || 349;

  const currencyString = currency.toUpperCase();

  // HSA/FSA is a US-only tax construct — keep the claim off non-US pages
  const seoDescription =
    countryCode === 'US'
      ? t('home.seo.description')
      : t('home.seo.descriptionIntl');

  const productSchema = generateProductSchema({
    name: 'Ultrahuman Ring AIR',
    description: seoDescription,
    imageUrl: getAssetUrl(
      usSeoImagePath(countryCode, '/web_v2/backgrounds/ring-air-hero.png'),
    ),
    brand: 'Ultrahuman',
    category: 'Wearable Technology',
    price: actualPrice,
    currency: currencyString,
    country: countryCode,
    availability: 'InStock',
    url: `https://www.ultrahuman.com/${regionSlug}/ring/buy/`,
    rating: {
      ratingValue: 4.7,
      reviewCount: 2500,
    },
  });

  const breadcrumbSchema = generateBreadcrumbSchema([
    {
      name: 'Home',
      url: 'https://www.ultrahuman.com',
    },
    {
      name: 'Ultrahuman Ring',
      url: 'https://www.ultrahuman.com/ring',
    },
  ]);

  const ringSchemas = [productSchema, breadcrumbSchema];

  const totalLoadCount = 2;
  let loadCount = 0;

  const loadCompleteHandle = () => {
    loadCount++;
    if (loadCount >= totalLoadCount) {
      setLoading(false);
    }
  };

  useIsomorphicLayoutEffect(() => {

    const timer = setTimeout(() => {
      setLoading(false);
    }, 3000);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (!isLoading) {
      requestAnimationFrame(() => ScrollTrigger.refresh());
    }
  }, [isLoading]);

  return (
    <main>
      {isLoading && (
        <div className={styles.root}>
          <Image src={LOADER_SRC} alt={t('home.loader.alt')} height="640" width="640" />
        </div>
      )}
      <JsonLd data={ringSchemas} />
      {/* {country && RamadanCountries.includes(country) ? <RamadanBanner /> : null} */}
      {/* CampaignBanner goes here */}
      {/* <RingMarketingBanner /> */}
      {/* <WomensHealthBanner /> */}
      <HeroSectionV2 onLoad={loadCompleteHandle} />
      {/* <UHHomeMarketingBanner /> */}
      <AccuracySection />
      <RingRotateRevolveRenderCanvas onLoad={loadCompleteHandle} />
      <InsideOutsideV2 />
      <CircleScaleClipImageV2 />
      <RingAssemblyRenderCanvas />
      <BentoSection1 />
      {/* <SleepSection /> */}
      {/* <Attention /> */}
      {/* <ImageTitleCtaSection
        backgroundColor="rgba(0,0,0,1)"
        image="web_v2/backgrounds/particle-ring-air.png"
        responsiveImage="web_v2/backgrounds/responsive/particle-ring-air.png"
        title={<>Feel the AIR</>}
        ctaLink="/ring/buy"
        ctaTitle="Buy now"
        cta2Title={
          <>
            Switch to AIR <CaretUp style={{ transform: 'rotate(90deg)' }} />
          </>
        }
        cta2Link="https://ring.ultrahuman.com"
      /> */}

      {/* M1 and R1 */}
      <HealthSpanSection
        imageMobile="landing/bg/m1_bg_responsive_tinted.png"
        title={
          <>
            {/* Discover <br /> metabolic <br />
            health with M1 */}
            {t('home.healthSpanSection.title.a')} <br />{' '}
            {t('home.healthSpanSection.title.b')} <br />
            {t('home.healthSpanSection.title.c')}
          </>
        }
        titleLarge
        description={t('home.healthSpanSection.description')}
        btnText={
          <>
            {t('home.healthSpanSection.btnText')}
          </>
        }
        textColor="white"
        ctaLink="/pricing"
      />
      <R1WithM1Section
        mblimageL="web_v2/backgrounds/iphone_14_max_R1-2.png"
        mblimageR="web_v2/backgrounds/iphone_14_max_M1-2.png"
        title={
          <>
            {t('home.r1withM1Section.title')}
          </>
        }
        titleLarge
        description={t('home.r1withM1Section.description')}
        textColor="white"
      />
    </main>
  );
};

const Ring = () => {
  return (
    <MasterTimelineProvider>
      <RingContent />
    </MasterTimelineProvider>
  );
};

export default Ring;
